import { Link } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { 
  BookOpen, 
  ChevronRight, 
  Clock,
  CheckCircle,
  Play,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';

export default function MyCourses() {
  const { courses, enrollments, loading } = usePortal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading courses...</p>
        </div>
      </div>
    );
  }

  // Group courses by status
  const inProgress = courses.filter(c => c.progress_percentage > 0 && c.progress_percentage < 100);
  const notStarted = courses.filter(c => c.progress_percentage === 0);
  const completed = courses.filter(c => c.progress_percentage === 100);

  const CourseCard = ({ course }) => (
    <Link to={`/portal/courses/${course.course_id}`}>
      <Card className="h-full hover:shadow-md transition-all hover:border-[#1E3A8A]/30 cursor-pointer">
        <CardContent className="p-5">
          {/* Course Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0">
              {course.code?.split('-')[0] || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-[#1E3A8A] bg-blue-50 px-2 py-0.5 rounded">
                {course.code}
              </span>
              <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2">{course.title}</h3>
            </div>
          </div>

          {/* Course Description */}
          <p className="text-sm text-gray-500 line-clamp-2 mb-4">{course.description}</p>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Progress</span>
              <span className="font-medium text-gray-900">{Math.round(course.progress_percentage)}%</span>
            </div>
            <Progress value={course.progress_percentage} className="h-2" />
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span>{course.completed_lessons} of {course.total_lessons} lessons</span>
              {course.progress_percentage === 100 ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-3 h-3" /> Complete
                </span>
              ) : course.progress_percentage > 0 ? (
                <span className="flex items-center gap-1 text-blue-600">
                  <Play className="w-3 h-3" /> In Progress
                </span>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (enrollments.length === 0) {
    return (
      <div className="space-y-8" data-testid="my-courses-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">My Courses</h1>
          <p className="text-gray-500">View and continue your enrolled courses</p>
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">No courses yet</h3>
            <p className="text-gray-500 mb-4">Enroll in a program to access courses</p>
            <Link to="/portal/program">
              <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90">
                Browse Programs
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-courses-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">My Courses</h1>
        <p className="text-gray-500">
          {courses.length} courses across {enrollments.length} program{enrollments.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{inProgress.length}</div>
            <div className="text-sm text-blue-700">In Progress</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-100">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{notStarted.length}</div>
            <div className="text-sm text-gray-700">Not Started</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-100">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{completed.length}</div>
            <div className="text-sm text-green-700">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* In Progress Courses */}
      {inProgress.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Continue Learning
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {inProgress.map(course => (
              <CourseCard key={course.course_id} course={course} />
            ))}
          </div>
        </section>
      )}

      {/* Not Started Courses */}
      {notStarted.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-gray-600" />
            Not Started
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {notStarted.map(course => (
              <CourseCard key={course.course_id} course={course} />
            ))}
          </div>
        </section>
      )}

      {/* Completed Courses */}
      {completed.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Completed
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {completed.map(course => (
              <CourseCard key={course.course_id} course={course} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
